import { Injectable, inject } from '@angular/core';
import { CrudService } from '@shared/modules/crud/crud.service';
import {MODELNAME } from './consorcios.config';
import { CocheraConsorcista } from './cocheras.service';
import { Timestamp } from '@angular/fire/firestore';
import { deleteField } from 'firebase/firestore';

export enum OccupancyRole {
    Owner = 'owner',
    Tenant = 'tenant',
    RealEstate = 'realEstate',
    Other = 'other'
}
export enum ConsorcistaRole {
    Consorcista = 'Consorcista',
    Arquitecto = "Arquitecto"
}

export const ConsorcistaRoleTranslate = {
    "propietario": "owner",
    "inquilino": "tenant"

}


export type Consorcista = {
    uid?: string;
    createdAt?: Date | Timestamp;
    firstName: string;
    lastName?: string;
    phoneArea?: string;
    phoneNumber?: string | number;
    countryCode?: string;
    tower?: string; //se corresponde al nombre de la torre, por eso es un string y no un numero
    floor?: string; //es string para que acepte valores como PB por ejemplo
    apartment?: string;
    role?: ConsorcistaRole;
    occupancyRole?: OccupancyRole;
    consorcioId?: string;
    wspId?: string;
    email?: string;
    //solo números. Lo carga el formulario de pedido de llave (AF298) si faltaba; no se pisa si ya existe
    dni?: string;
    //si es true esta consorcista es solo cochera, es decir no tiene departamento asociado, solo cochera
    onlyGarage?: boolean;
    emergencyContact?: {
        countryCode?: string;
        firstName: string;
        lastName: string;
        phoneArea: string;
        phoneNumber: string | number;
        accessKey?: boolean;
        //'relation' se trata de la relacion con este contacto de emergencia, por ejemplo "padre" o "vecino"
        relation?: string;
    };
    /* Datos SOLO PARA INQUILINO */
    contractType?: 'realEstate' | 'direct';
    realEstate?: {
        companyName: string;
        countryCode?: string;
        phoneArea: string;
        phoneNumber: string | number;
    };
    owner?: {
        firstName: string;
        lastName: string;
        countryCode?: string;
        phoneArea: string;
        phoneNumber: string | number;
    };
}

//teléfono de un wspId de chat ('5493416533005@s.whatsapp.net') separado como lo guarda el alta manual de consorcistas
//(countryCode + phoneArea + phoneNumber, y wspId = los tres juntos). Solo para Argentina: 549 o 54 + 10 dígitos.
//El área es una suposición (11 = 2 dígitos, el resto 3); si en realidad es de 4, el wspId que se vuelve a armar da igual.
//undefined si el número no tiene esa forma
export function telefonoDesdeWspId(wspId?: string | null): { countryCode: string; phoneArea: string; phoneNumber: string } | undefined {
    const digitos = (wspId ?? '').split('@')[0].replace(/\D/g, '');
    const countryCode = digitos.startsWith('549') ? '549' : digitos.startsWith('54') ? '54' : '';
    const local = digitos.slice(countryCode.length);
    if (!countryCode || local.length !== 10) return undefined;
    const largoArea = local.startsWith('11') ? 2 : 3;
    return { countryCode, phoneArea: local.slice(0, largoArea), phoneNumber: local.slice(largoArea) };
}

//Firestore no acepta campos undefined (tira 'Unsupported field value: undefined' y no guarda nada).
//Al crear se sacan; al editar se borran del documento (un campo que se vació en el formulario)
function sinUndefined(data: any) {
    return Object.fromEntries(Object.entries(data ?? {}).filter(([, valor]) => valor !== undefined));
}
function undefinedComoBorrado(data: any) {
    return Object.fromEntries(Object.entries(data ?? {}).map(([campo, valor]) => [campo, valor === undefined ? deleteField() : valor]));
}

export const SUBCOLLECTIONCONSORCISTASPENDING = 'consorcistasPendientes';

export type consorcistaStatus = 'pending' | 'accepted' | 'rejected';

export type PendingInteractionType = 'complain' | 'question';

export type ConsorcistaPending = Consorcista & {
    status: consorcistaStatus;
    cocheras?: Partial<CocheraConsorcista>[] | Partial<CocheraConsorcista>;
    //consulta o reclamo que el consorcista dejó al momento de registrarse. Se convierte en Complain/Question recién al aceptar el registro.
    pendingInteraction?: {
        type: PendingInteractionType;
        text: string;
    };
    //lo agrega el formulario de registro si la administración tiene activado "aceptar automáticamente". Lo cierra la función auto-close-registration.
    autoClose?: boolean;
};

@Injectable({
    providedIn: 'root',
})
export class ConsorcistasService {
    model = MODELNAME;
    crudService = inject(CrudService);
    subcollection = 'consorcistas';
    subcollectionPending = SUBCOLLECTIONCONSORCISTASPENDING;

    getAll(id: string) {
        this.crudService.setModel(
            this.model + '/' + id + '/' + this.subcollection
        );
        return this.crudService.findAll<any>();
    }
    getAllPending(id: string) {
        this.crudService.setModel(
            this.model + '/' + id + '/' + this.subcollectionPending
        );
        return this.crudService.findAll<any>();
    }

    create(consorcioId: string, data: any) {
        if (data.floor) {
            data.floor = data.floor.toUpperCase();
        }
        if (data.apartment) {
            data.apartment = data.apartment.toUpperCase();
        }
        const path = this.model + '/' + consorcioId + '/' + this.subcollection;
        this.crudService.setModel(path);
        return this.crudService.add(sinUndefined(data));
    }
    createPending(consorcioId: string, data: any) {
        if (data.floor) {
            data.floor = data.floor.toUpperCase();
        }
        if (data.apartment) {
            data.apartment = data.apartment.toUpperCase();
        }
        const path = this.model + '/' + consorcioId + '/' + this.subcollectionPending;
        this.crudService.setModel(path);
        return this.crudService.add(data);
    }

    update(consorcioId: string, consorcistaId: string, data: any) {
        const path =
            this.model +
            '/' +
            consorcioId +
            '/' +
            this.subcollection +
            '/' +
            consorcistaId;
        this.crudService.setModel(path);
        return this.crudService.update('', undefinedComoBorrado(data));
    }
    updatePending(consorcioId: string, consorcistaId: string, data: any) {
        const path =
            this.model +
            '/' +
            consorcioId +
            '/' +
            this.subcollectionPending +
            '/' +
            consorcistaId;
        this.crudService.setModel(path);
        return this.crudService.update('', data);
    }

    getAllByConsorcio(id: string) {
        this.crudService.setModel(
            this.model + '/' + id + '/' + this.subcollection
        );
        return this.crudService.findAll<any>();
    }

    getConsorcistaById(consorcioId: string, consorcistaId: string) {
        this.crudService.setModel(
            this.model +
            '/' +
            consorcioId +
            '/' +
            this.subcollection +
            '/' +
            consorcistaId
        );
        return this.crudService.findFirst<any>('');
    }

    delete(consorcioId: string, consorcistaId: string) {
        const path =
            this.model + '/' + consorcioId + '/' + this.subcollection + '/' + consorcistaId;;
        this.crudService.setModel(path);
        return this.crudService.delete('');
    }
    deleteMany(consorcioId: string, consorcistas: string[]) {
        const path =
            this.model + '/' + consorcioId + '/' + this.subcollection;
        this.crudService.setModel(path);
        return this.crudService.deleteMany(consorcistas);
    }

    createMany(consorcioId: string, data: Consorcista[]) {
        //convertir a mayusculas los pisos
        data.map(consorcista => {
            if (consorcista.floor) {
                consorcista.floor = consorcista.floor.toUpperCase();
            }
            if (consorcista.apartment) {
                consorcista.apartment = consorcista.apartment.toUpperCase();
            }
            return consorcista;
        })
        const path = `${this.model}/${consorcioId}/${this.subcollection}`;
        this.crudService.setModel(path);
        return this.crudService.addMany(data);
    }

    getByUnit(consorcioId: string, floor: string, apartment: string) {
        this.crudService.setModel(this.model + '/' + consorcioId + '/' + this.subcollection);

        const filter: any = [{
            field: 'floor',
            operator: '==',
            value: floor.toUpperCase(),
        }, {
            field: 'apartment',
            operator: '==',
            value: apartment.toUpperCase(),
        }
        ];

        return this.crudService.findWhere<any>(filter, []);
    }

    searchByUnitAndName(consorcioId: string, floor: string, apartment: string, name: string, lastName: string) {
        this.crudService.setModel(this.model + '/' + consorcioId + '/' + this.subcollection);

        const filter: any = [];

        if (floor && floor.trim() !== '') {
            filter.push({ field: 'floor', operator: '==', value: floor.toUpperCase() });
        }

        if (apartment && apartment.trim() !== '') {
            filter.push({ field: 'apartment', operator: '==', value: apartment.toUpperCase() });
        }

        const order: any[] = [];

        if (name && name.trim() !== '') {
            filter.push({ field: 'firstName', operator: '>=', value: name });
            filter.push({ field: 'firstName', operator: '<=', value: name + '\uf8ff' });
            order.push({ orderBy: 'firstName', orderByDirection: 'asc' });
        }

        if (lastName && lastName.trim() !== '') {
            filter.push({ field: 'lastName', operator: '>=', value: lastName });
            filter.push({ field: 'lastName', operator: '<=', value: lastName + '\uf8ff' });
            order.push({ orderBy: 'lastName', orderByDirection: 'asc' });
        }

        return this.crudService.findWhere<any>(filter, order);
    }
    async countByUnitAndRole(consorcioId: string, floor: string, apartment: string,occupancyRole:OccupancyRole, tower?: string) {
        this.crudService.setModel(this.model + '/' + consorcioId + '/' + this.subcollection);

        const filter: any = [];

        filter.push({ field: 'floor', operator: '==', value: floor.toUpperCase() });
        filter.push({ field: 'apartment', operator: '==', value: apartment.toUpperCase() });    
        filter.push({ field: 'occupancyRole', operator: '==', value: occupancyRole });

        if (tower && tower.trim() !== '') {
            //no pasamos a mayuscula, porque las torres se guardan respetando sus mayusculas y minusculas
            filter.push({ field: 'tower', operator: '==', value: tower });
        }

        return await this.crudService.findWhereCount(filter, []);
    }




    getConsorcistaIdByWspId(consorcioId: string, wspId: string) {
        this.crudService.setModel(this.model + '/' + consorcioId + '/' + this.subcollection);
        const filter: any = [{
            field: 'wspId',
            operator: '==',
            value: wspId,
        }
        ];
        return this.crudService.findWhere<any>(filter, []);
    }

    // Agregarle cuando exista el parametro el id del usuario en este filtro.
    searchByWspId(wspId: string) {

        const path = this.subcollection;
        this.crudService.setModel(path);

        const filter: any = [
            {
                field: 'wspId',
                operator: '==',
                value: wspId,
            }
        ]

        return this.crudService.searchCollectionGroupMany<any>(filter, []);
    }
    // Sin implementar ya que falta un parametro
    // getAllByAdministration(administrationId:string) {
    //
    //     const path =this.subcollection;
    //     this.crudService.setModel(path);
    //
    //         const filter:any =[
    //         {
    //             field: 'wspId',
    //             operator: '==',
    //             value: administrationId,
    //         }
    //     ]
    //
    //     return this.crudService.searchCollectionGroupMany<any>(filter,[]);
    // }

}
